import { Component, Input, OnDestroy, OnInit, inject } from '@angular/core';
import { formatDate } from '@angular/common';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { debounceTime, take, takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';

import { IContractParams } from '../../models/catalog.models';
import { CpqFacade } from '../../facades/cpq.facade';
import { MarketingFacade } from '../../facades/marketing.facade';
import { FileType, FileUtils } from '../../utils/file.utils';
import {
  ICPQConfigurationSummary,
  IPricingSummary,
  ICPQResponseIncludedAttributes,
} from '../../models/cpq.models';
import { ConfigurationFacade } from '../../facades/configuration.facade';
import { I18nService } from '../../services';
import { CustomerFacade } from '../../facades/customer.facade';
import { AppUtils } from '../../utils/app.utils';
import { CpqUtils } from '../../utils/cpq.utils';
import { EAddressType, EUserRoles } from '../../configurations/common';
import { IAddress } from '../../models/common.models';
import {
  IConfigurationSummaryContractSystemInformation,
  IConfigurationSummaryDetail,
  IConfigurationSummaryProduct,
} from './configuration-summary.models';

@Component({
  selector: 'app-page-configuration-summary',
  templateUrl: './configuration-summary.component.html',
  styleUrls: ['./configuration-summary.component.scss'],
  standalone: false,
})

export class ConfigurationSummaryComponent implements OnInit, OnDestroy {
  private readonly router: Router = inject(Router);
  private readonly cpqFacade: CpqFacade = inject(CpqFacade);
  private readonly translate: I18nService = inject(I18nService);
  private readonly activatedRoute: ActivatedRoute = inject(ActivatedRoute);
  private readonly customerFacade: CustomerFacade = inject(CustomerFacade);
  private readonly marketingFacade: MarketingFacade = inject(MarketingFacade);
  private readonly configFacade: ConfigurationFacade = inject(ConfigurationFacade);

  contractSystemInformation!: IConfigurationSummaryContractSystemInformation;
  serviceParams: IContractParams = {
    selectedService: '',
    'fl-number': '',
    'rel-product-sysivk': '',
    configId: '',
  };
  cart: any;
  loading: boolean = true;
  itemsWithDetails!: ICPQResponseIncludedAttributes;
  products: IConfigurationSummaryProduct[] = [];
  cartId!: string;
  configId!: string;
  priceSummary!: IPricingSummary;
  businessAddresses: IAddress[] = [];
  isCpqConfigInUrl: boolean = false;
  ecommerceTeamEmailAddress!: string;
  customerBusinessUnitName!: string;
  showModalAddressWrong: boolean = false;
  addressType: EAddressType = EAddressType.SOLD_TO;
  configurationGenerationInProgress: boolean = false;

  @Input() loggedUserRoles!: EUserRoles[];

  private readonly unsubscribe$: Subject<void> = new Subject<void>();
  private readonly docDownloadErrorMsgKey: string = 'configuration-summary.doc-download-error-message';
  private docDownloadErrorMsg!: string;
  private readonly serviceConfigUrl: string = '/service-configuration';
  private readonly quoteSummaryUrl: string = '/quote-summary';

  ngOnInit(): void {
    this.setDownloadErrorMessage();
    this.activatedRoute.queryParams.pipe(debounceTime(0), take(1)).subscribe((params: Params) => {
      this.serviceParams.selectedService = params.selectedService ?? '';
      this.serviceParams['fl-number'] = params['fl-number'] ?? '';
      this.serviceParams['rel-product-sysivk'] = params['rel-product-sysivk'] ?? '';
      this.serviceParams.configId = params.configId ?? '';
      if (this.serviceParams.configId === '') {
        this.loading = false;
      } else {
        this.isCpqConfigInUrl = true;
        this.marketingFacade.selectCartId().pipe(takeUntil(this.unsubscribe$))
          .subscribe(cartId => {
            if (cartId) {
              this.cartId = cartId;
              this.loadSystemContractInformation();
            }
          });
      }
    });
    this.getUserData();
    this.getStoreEmailAddress();
    this.selectCart();
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  downloadConfiguration(): void {
    this.configurationGenerationInProgress = true;
    this.cpqFacade.getConfigurationSummaryPdfFile(this.cartId, this.itemsWithDetails.sku ?? '', this.serviceParams.configId ?? '')
      .pipe(take(1))
      .subscribe({
          next: (file: Blob) => {
            FileUtils.saveAndOpenFile(
              file,
              FileType.PDF,
              `Configuration_summary_${this.serviceParams['fl-number']}_${this.cartId}`);
            this.configurationGenerationInProgress = false;
          },
          error: () => {
            this.configFacade.setAlert({
              type: 'error',
              message: this.docDownloadErrorMsg,
            });
            this.configurationGenerationInProgress = false;
          },
        },
      );
  }

  showModalWrongAddress(): void {
    this.showModalAddressWrong = true;
  }

  getMultipleFunctionalLocations(cart: any): string[] {
    const functionalLocationsInArray: string[] = [];
    functionalLocationsInArray.push(cart?.attributes?.systemDetails?.siemensEquipmentId);

    return functionalLocationsInArray;
  }

  editAllSections(): void {
    this.router.navigate([this.serviceConfigUrl], {queryParams: this.serviceParams}).then();
  }

  goToQuoteSummary(): void {
    this.router.navigate([this.quoteSummaryUrl], {queryParams: this.serviceParams}).then();
  }

  private getStoreEmailAddress(): void {
    this.ecommerceTeamEmailAddress = AppUtils.getCurrentStore().ecommerceTeamEmailAddress ?? '';
  }

  private getUserData(): void {
    this.customerFacade.selectCompanyBusinessUnit()
      .pipe(take(1))
      .subscribe(businessUnit => {
        this.customerBusinessUnitName = businessUnit?.name ?? '';
      });
  }

  private selectCart(): void {
    this.marketingFacade.selectCart().pipe(
      takeUntil(this.unsubscribe$),
    ).subscribe(cart => {
      this.cart = cart;
    });
  }

  private loadSystemContractInformation(): void {
    this.marketingFacade.getCartItems(this.cartId).pipe(
      takeUntil(this.unsubscribe$),
    ).subscribe(cartItems => {
      const includedItems: any[] = cartItems.included ?? [];
      this.itemsWithDetails = includedItems.find(
        (item: any) => item.type === 'items' && item.attributes.cpqConfigId === this.serviceParams.configId)?.attributes ?? {};
      const skuName: string = includedItems.find(
        (item: any) => item.type === 'concrete-products' && item.attributes.sku === this.itemsWithDetails.sku)?.attributes.name ?? '-';
      const systemDetails: any = this.itemsWithDetails.systemDetails;
      this.contractSystemInformation = {
        address: CpqUtils.cpqAddressToString(this.itemsWithDetails.customerSoldToAddress),
        details: [
          {
            label: 'configuration-summary.service-plan',
            data: skuName,
          },
          {
            label: 'configuration-summary.contract-start-date',
            data: systemDetails?.contractStartDate ?
              formatDate(systemDetails.contractStartDate, 'MM/dd/yyyy', 'en') : '-',
          },
          {
            label: 'configuration-summary.contract-end-date',
            data: systemDetails?.contractExpirationDate ?
              formatDate(systemDetails.contractExpirationDate, 'MM/dd/yyyy', 'en') : '-',
          },
          {
            label: 'configuration-summary.end-of-support',
            data: systemDetails?.endOfSupport ?
              formatDate(systemDetails.endOfSupport, 'MM/dd/yyyy', 'en') : '-',
          },
        ],
      };

      if (this.contractSystemInformation.address) {
        this.businessAddresses = [this.itemsWithDetails.customerSoldToAddress];
      } else {
        this.businessAddresses = [
          {
            address1: 'empty',
            address2: 'empty',
            city: 'empty',
            company: 'empty',
            country: 'empty',
            firstName: 'empty',
            id: 'empty',
            isDefaultBilling: false,
            isDefaultShipping: false,
            iso2Code: 'empty',
            lastName: 'empty',
            phone: 'empty',
            salutation: 'empty',
            zipCode: 'empty',
          },
        ];
      }
      this.loadConfigurationSummary();
    });
  }

  private loadConfigurationSummary(): void {
    this.cpqFacade.reconfigureService(this.cartId, this.itemsWithDetails.sku ?? '', this.serviceParams.configId ?? '').pipe(
      takeUntil(this.unsubscribe$),
    ).subscribe({
        next: data => {
          const config: ICPQConfigurationSummary = data.data.attributes.cpqResponse;
          this.products = config.bom.reduce((acc: IConfigurationSummaryProduct[], next): IConfigurationSummaryProduct[] => {
            const itemPrice: any = config.pricing.rows.find(item => item.PartNumber === next.attributes.SKU);
            const groupIndex: number = acc.findIndex((group: IConfigurationSummaryProduct) => group.name === next.attributes.groupDescription);
            const lineItem: IConfigurationSummaryDetail = {
              name: next.description,
              price: itemPrice?.ListPrice,
              currency: itemPrice?.Currency,
            };
            if (groupIndex !== -1) {
              acc[groupIndex].details.push(lineItem);
            } else {
              acc.push({
                name: next.attributes.groupDescription,
                details: [lineItem],
              });
            }
            return acc;
          }, []);
          this.priceSummary = config.pricing.summary;
          this.loading = false;
        },
        error: () => {
          this.isCpqConfigInUrl = false;
          this.loading = false;
        },
      },
    );
  }

  private setDownloadErrorMessage(): void {
    this.translate.getTranslationByKey(this.docDownloadErrorMsgKey)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(
        message => this.docDownloadErrorMsg = message,
      );
  }
}
